import { createFileRoute } from "@tanstack/react-router";
import { useIsFetching, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { format, subDays } from "date-fns";
import { PageHeader } from "@/components/layout/page-header";
import { AnalyticsDashboard } from "@/components/dashboard/analytics-dashboard";
import { DateRangeFilter } from "@/components/data/date-range-filter";
import { RefreshButton } from "@/components/data/refresh-button";

const searchSchema = z.object({
  from: z.string().optional(),
  to: z.string().optional(),
});

/** Rango por defecto: últimos 30 días (hoy incluido) en `YYYY-MM-DD`. */
function defaultRange() {
  const today = new Date();
  return {
    from: format(subDays(today, 29), "yyyy-MM-dd"),
    to: format(today, "yyyy-MM-dd"),
  };
}

export const Route = createFileRoute("/_authenticated/dashboard/")({
  validateSearch: searchSchema,
  component: DashboardPage,
});

function DashboardPage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const qc = useQueryClient();
  const { t } = useTranslation();

  const def = defaultRange();
  const from = search.from ?? def.from;
  const to = search.to ?? def.to;

  const fetching =
    useIsFetching({ queryKey: ["executions", "analytics"] }) > 0;

  return (
    <>
      <PageHeader
        title={t("dashboard.title")}
        subtitle={t("dashboard.subtitle")}
        actions={
          <div className="flex items-center gap-2">
            <RefreshButton
              onClick={() =>
                qc.invalidateQueries({
                  queryKey: ["executions", "analytics"],
                })
              }
              spinning={fetching}
            />
            <DateRangeFilter
              from={from}
              to={to}
              onChange={(range) =>
                navigate({
                  search: (prev) => ({
                    ...prev,
                    from: range.from,
                    to: range.to,
                  }),
                  replace: true,
                })
              }
            />
          </div>
        }
      />

      <AnalyticsDashboard from={from} to={to} />
    </>
  );
}
